import { useState } from 'react';
import { Pencil, Plus, Sparkle, Trash, X } from '@phosphor-icons/react';
import type { StudyQuestion } from '@cvs/shared';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Modal } from './ui';

/**
 * Per-class interview-style practice-question editor. AI-drafts questions from
 * the class materials (summary/slides/notes/transcript/lab); the instructor
 * reviews, edits, and adds/removes. Self-contained.
 */
export function StudyManager({ moduleId }: { moduleId: string }) {
  const [refresh, setRefresh] = useState(0);
  const state = useAsync(() => api.admin.studyQuestions(moduleId), [moduleId, refresh]);
  const questions = state.data ?? [];
  const [modal, setModal] = useState<{ question?: StudyQuestion } | null>(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>, after?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setRefresh((n) => n + 1);
      after?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    setGenerating(true);
    setError(null);
    try {
      await api.admin.generateStudyQuestions(moduleId, 6);
      setRefresh((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not generate questions');
    } finally {
      setGenerating(false);
    }
  }

  if (state.loading) return <p className="px-4 py-2 text-sm text-slate-400">Loading practice questions…</p>;

  return (
    <div className="border-t border-slate-100 px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="label-mono">Practice Q&amp;A</span>
          <p className="text-xs text-slate-500">
            Interview-style questions. Students answer in their own words and an AI coach scores them.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={Sparkle} loading={generating} onClick={generate}>
            Generate from materials
          </Button>
          <Button variant="secondary" icon={Plus} onClick={() => setModal({})}>
            Add question
          </Button>
        </div>
      </div>
      {error && <p className="mb-2 text-sm text-rose-600">{error}</p>}

      <ol className="space-y-2">
        {questions.map((q, i) => (
          <li key={q.id} className="rounded-lg bg-white p-3 text-sm shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium text-slate-700">
                {i + 1}. {q.prompt}
              </span>
              <span className="flex shrink-0 gap-1">
                <button className="btn-ghost p-1.5" aria-label="Edit question" onClick={() => setModal({ question: q })}>
                  <Pencil size={16} />
                </button>
                <button
                  className="btn-ghost p-1.5 text-rose-600"
                  aria-label="Delete question"
                  onClick={() => void run(() => api.admin.deleteStudyQuestion(q.id))}
                >
                  <Trash size={16} />
                </button>
              </span>
            </div>
            {q.modelAnswer && <p className="mt-1 text-xs text-slate-500">Model: {q.modelAnswer}</p>}
            {q.keyPoints.length > 0 && (
              <ul className="mt-1.5 flex flex-wrap gap-1">
                {q.keyPoints.map((k, ki) => (
                  <li key={ki} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                    {k}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
        {questions.length === 0 && (
          <li className="text-sm text-slate-400">
            No practice questions yet — “Generate from materials” drafts them from the class summary, slides, notes and
            lab. Review and edit them afterwards.
          </li>
        )}
      </ol>

      {modal && (
        <Modal open onClose={() => setModal(null)} title={modal.question ? 'Edit question' : 'Add question'}>
          <StudyQuestionForm
            key={modal.question?.id ?? 'new'}
            initial={modal.question}
            busy={busy}
            onCancel={() => setModal(null)}
            onSubmit={(v) =>
              run(
                () =>
                  modal.question
                    ? api.admin.updateStudyQuestion(modal.question.id, v)
                    : api.admin.createStudyQuestion(moduleId, v),
                () => setModal(null),
              )
            }
          />
        </Modal>
      )}
    </div>
  );
}

function StudyQuestionForm({
  initial,
  busy,
  onSubmit,
  onCancel,
}: {
  initial?: StudyQuestion;
  busy: boolean;
  onSubmit: (v: { prompt: string; modelAnswer: string; keyPoints: string[] }) => void;
  onCancel: () => void;
}) {
  const [prompt, setPrompt] = useState(initial?.prompt ?? '');
  const [modelAnswer, setModelAnswer] = useState(initial?.modelAnswer ?? '');
  const [keyPoints, setKeyPoints] = useState<string[]>(initial?.keyPoints?.length ? initial.keyPoints : ['']);

  const setKp = (i: number, v: string) => setKeyPoints((k) => k.map((x, xi) => (xi === i ? v : x)));

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          prompt: prompt.trim(),
          modelAnswer: modelAnswer.trim(),
          keyPoints: keyPoints.map((k) => k.trim()).filter(Boolean),
        });
      }}
    >
      <label className="block">
        <span className="label-mono mb-1 block">Question (open-ended)</span>
        <textarea
          className="input"
          rows={2}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g. Explain how token protection stops a stolen token — and what it doesn't cover."
          required
        />
      </label>
      <label className="block">
        <span className="label-mono mb-1 block">Model answer</span>
        <textarea
          className="input"
          rows={4}
          value={modelAnswer}
          onChange={(e) => setModelAnswer(e.target.value)}
          placeholder="A strong, specific answer students can study from…"
        />
      </label>
      <div>
        <span className="label-mono mb-1 block">Key points (what the AI judge checks for)</span>
        <div className="space-y-2">
          {keyPoints.map((k, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="input" value={k} onChange={(e) => setKp(i, e.target.value)} placeholder={`Key point ${i + 1}`} />
              {keyPoints.length > 1 && (
                <button
                  type="button"
                  className="btn-ghost p-1.5 text-rose-600"
                  aria-label="Remove key point"
                  onClick={() => setKeyPoints((kp) => kp.filter((_, xi) => xi !== i))}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
        <Button type="button" variant="ghost" icon={Plus} className="mt-2" onClick={() => setKeyPoints((kp) => [...kp, ''])}>
          Add key point
        </Button>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={busy} disabled={!prompt.trim()}>
          Save
        </Button>
      </div>
    </form>
  );
}
